/**
 * `/leads/:id/edit` — the same five-section form as `/leads/new`, prefilled
 * from `GET /api/leads/:id`. `lead.update` requires connectivity per
 * docs/plan.md → Offline Resilience ("`lead.update`... require[s]
 * connectivity and their controls are disabled without it") — Save is
 * disabled offline via `useIsOffline()`, with an explanation.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Why this calls `apiRequest`/`apiRequestMultipart` directly rather than the
 * `enqueueLeadUpdate` seam in lib/offline
 * ─────────────────────────────────────────────────────────────────────────
 * `enqueueLeadUpdate` exists and DOES drain through the exact same
 * multipart/JSON PATCH this file builds — it would work. But it resolves as
 * soon as the mutation is written to Dexie, not once the server has actually
 * answered, so its caller cannot show a real inline success/failure the way
 * this screen needs to (this is precisely the offline-queued UX `lead.create`
 * wants and `lead.update` deliberately does NOT — see the outbox kind
 * comment in lib/db.ts). Calling `apiRequest`/`apiRequestMultipart` directly
 * gives a synchronous, awaited result to react to, matching "Save disabled
 * offline, otherwise a normal online save" — the behaviour actually asked
 * for here.
 *
 * A related gap: neither `UpdateLeadInput` nor `CreateLeadInput` carries a
 * note for a lead's stage move except through the dedicated
 * `POST /api/leads/:id/stage` (`StageChangeInput`) — see
 * lead-form-values.ts's `stageNote` doc comment. So when the stage actually
 * changed, this file calls that endpoint separately (for the note) and omits
 * `stage` from the PATCH body entirely, rather than double-writing it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CARD_BACK_FIELD,
  CARD_FRONT_FIELD,
  CreateLeadInput,
  LEAD_PAYLOAD_FIELD,
  normalizeMoneyInput,
  UpdateLeadInput,
  type LeadDetail,
  type LeadWithItems,
  type StageChangeInput
} from '@paradise/shared';

import {
  LeadCaptureForm,
  type LeadCaptureSaveResult,
  type LeadCaptureSubmission
} from '@/components/capture/lead-capture-form';
import { toCreateLeadItemInput } from '@/components/capture/item-mapping';
import { phoneFieldValue, valuesFromCreateInput, type LeadFormValues } from '@/components/capture/lead-form-values';
import { emptyToNull, phoneToNull } from '@/components/capture/money-input';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { apiRequest, apiRequestMultipart, describeApiFailure, type ApiResult } from '@/lib/api/http';
import { useCaptureDraft, type DraftItem } from '@/lib/capture-draft';
import { cardOriginalMediaUrl, mediaUrl } from '@/lib/media';
import { db } from '@/lib/db';
import {
  amendLeadCreate,
  readDraft,
  readMirror,
  refreshLeadsMirror,
  useIsOffline,
  type CardCapture
} from '@/lib/offline';

export const Route = createFileRoute('/_app/leads/$leadId/edit')({
  staticData: { hideShellChrome: true },
  component: EditLeadRoute
});

/**
 * What this screen is editing. A `draft` is a lead still sitting in the outbox:
 * it has no server id, so `leadId` is its `clientUuid`, and saving amends the
 * queued create rather than PATCHing anything — which is why that path works
 * with no connection and the `server` path does not.
 */
type EditTarget =
  | { kind: 'server'; lead: LeadDetail }
  | {
      kind: 'draft';
      clientUuid: string;
      ref: number | null;
      input: CreateLeadInput;
      front: CardCapture | null;
      back: CardCapture | null;
    };

async function loadEditTarget(leadId: string): Promise<EditTarget | null> {
  const result = await apiRequest<LeadDetail>(`/api/leads/${leadId}`);
  if (result.kind === 'ok') return { kind: 'server', lead: result.data };
  if (result.kind === 'unauthenticated') return null;

  // Offline, or a `leadId` that is really a `clientUuid`. A local draft is the
  // editable case; a mirrored lead only renders (Save stays disabled offline),
  // which still beats "Could not load this buyer" on a lead the device holds.
  const draft = await readDraft(leadId);
  if (draft) {
    const [front, back] = await Promise.all([db.cardBlobs.get(leadId), db.cardBackBlobs.get(leadId)]);
    return {
      kind: 'draft',
      clientUuid: draft.clientUuid,
      ref: draft.ref,
      input: draft.input,
      front: front ? { bytes: front.bytes, contentType: front.contentType } : null,
      back: back ? { bytes: back.bytes, contentType: back.contentType } : null
    };
  }

  const mirrored = (await readMirror()).find(row => row.id === leadId);
  return mirrored
    ? { kind: 'server', lead: { ...mirrored, history: [], quoteRequests: [], selections: [], quotations: [] } }
    : null;
}

/**
 * Amend the queued create instead of PATCHing. Same `clientUuid`, so the entry
 * that eventually reaches the server carries the edit — see `amendLeadCreate`
 * for why a second outbox entry would be wrong.
 */
async function saveDraftEdit(
  target: Extract<EditTarget, { kind: 'draft' }>,
  submission: LeadCaptureSubmission
): Promise<LeadCaptureSaveResult> {
  const { values, cardFront, cardBack, cardFrontRemoved, cardBackRemoved } = submission;

  const input: CreateLeadInput = {
    ...target.input,
    clientUuid: target.clientUuid,
    eventId: values.eventId,
    items: useCaptureDraft.getState().items.map(toCreateLeadItemInput),
    company: values.company.trim(),
    contactName: emptyToNull(values.contactName),
    phone: phoneToNull(values.phone),
    email: emptyToNull(values.email),
    city: emptyToNull(values.city),
    country: emptyToNull(values.country),
    stage: values.stage,
    stageNote: emptyToNull(values.stageNote),
    advance: normalizeMoneyInput(values.advance),
    paymentMode: values.paymentMode,
    followUpDate: emptyToNull(values.followUpDate),
    notes: emptyToNull(values.notes)
  };

  const parsed = CreateLeadInput.safeParse(input);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    const where = issue && issue.path.length > 0 ? `${String(issue.path[0])}: ` : '';
    return { ok: false, message: issue ? `${where}${issue.message}` : 'Check the form — something is not valid yet.' };
  }

  await amendLeadCreate(target.clientUuid, parsed.data, {
    front: cardFront ?? undefined,
    back: cardBack ?? undefined,
    frontRemoved: cardFrontRemoved,
    backRemoved: cardBackRemoved
  });
  return { ok: true };
}

function toFormValues(lead: LeadDetail): LeadFormValues {
  return {
    company: lead.company,
    contactName: lead.contactName ?? '',
    phone: phoneFieldValue(lead.phone, lead.country),
    city: lead.city ?? '',
    country: lead.country ?? '',
    email: lead.email ?? '',
    eventId: lead.eventId,
    stage: lead.stage,
    stageNote: '',
    advance: lead.advance ?? '',
    paymentMode: lead.paymentMode ?? null,
    notes: lead.notes ?? '',
    followUpDate: lead.followUpDate ?? ''
  };
}

function saveResultFrom<T>(result: ApiResult<T>): LeadCaptureSaveResult {
  if (result.kind === 'ok') return { ok: true };
  return {
    ok: false,
    message: describeApiFailure(result, {
      offline: 'Lost connection while saving. Try again once you are back online.',
      unauthenticated: 'Your session has expired. Sign in again to save changes.'
    })
  };
}

async function saveLeadEdit(lead: LeadDetail, submission: LeadCaptureSubmission): Promise<LeadCaptureSaveResult> {
  const { values, cardFront, cardBack, cardFrontRemoved, cardBackRemoved } = submission;

  const items = useCaptureDraft.getState().items.map(toCreateLeadItemInput);
  const stageChanged = values.stage !== lead.stage;

  const patch: UpdateLeadInput = {
    items,
    company: values.company.trim(),
    contactName: emptyToNull(values.contactName),
    phone: phoneToNull(values.phone),
    email: emptyToNull(values.email),
    city: emptyToNull(values.city),
    country: emptyToNull(values.country),
    advance: normalizeMoneyInput(values.advance),
    paymentMode: values.paymentMode,
    followUpDate: emptyToNull(values.followUpDate),
    notes: emptyToNull(values.notes),
    // `stage` intentionally omitted — see this file's header comment.
    // A removed side with no replacement clears BOTH its cover and its retained
    // original; a new capture below overwrites them via the multipart file part.
    // An unchanged side is omitted entirely (server leaves both keys alone).
    ...(cardFront === null && cardFrontRemoved ? { cardPhotoKey: null, cardFrontOriginalKey: null } : {}),
    ...(cardBack === null && cardBackRemoved ? { cardBackKey: null, cardBackOriginalKey: null } : {})
  };

  const parsedPatch = UpdateLeadInput.safeParse(patch);
  if (!parsedPatch.success) {
    return { ok: false, message: 'Check the form — something is not valid yet.' };
  }

  if (stageChanged) {
    const stageBody: StageChangeInput = { stage: values.stage, note: emptyToNull(values.stageNote) };
    const stageResult = await apiRequest<LeadWithItems>(`/api/leads/${lead.id}/stage`, {
      method: 'POST',
      body: stageBody
    });
    const stageOutcome = saveResultFrom(stageResult);
    if (!stageOutcome.ok) return stageOutcome;
  }

  let patchResult: ApiResult<LeadWithItems>;
  if (cardFront || cardBack) {
    const form = new FormData();
    form.append(LEAD_PAYLOAD_FIELD, JSON.stringify(parsedPatch.data));
    if (cardFront)
      form.append(CARD_FRONT_FIELD, new Blob([cardFront.bytes], { type: cardFront.contentType }), 'card-front.jpg');
    if (cardBack)
      form.append(CARD_BACK_FIELD, new Blob([cardBack.bytes], { type: cardBack.contentType }), 'card-back.jpg');
    patchResult = await apiRequestMultipart<LeadWithItems>(`/api/leads/${lead.id}`, form, { method: 'PATCH' });
  } else {
    patchResult = await apiRequest<LeadWithItems>(`/api/leads/${lead.id}`, { method: 'PATCH', body: parsedPatch.data });
  }

  return saveResultFrom(patchResult);
}

/** `POST /api/leads/:id/archive` — an immediate, online-only action, not queued through the outbox. */
async function archiveLead(leadId: string): Promise<ApiResult<LeadWithItems>> {
  return apiRequest<LeadWithItems>(`/api/leads/${leadId}/archive`, { method: 'POST' });
}

function EditLeadRoute() {
  const { leadId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isOffline = useIsOffline();
  const setItems = useCaptureDraft(state => state.setItems);
  const seededForRef = useRef<string | null>(null);
  const clearDraft = useCaptureDraft(state => state.clear);

  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const { data: target, isLoading } = useQuery({
    // Own key, NOT ['lead-detail', …]: the detail screen caches a different
    // value shape ({ detail, source }) under that key, and its stage/restore
    // mutations invalidate it — sharing the slot would cross-churn and hand
    // back the wrong shape.
    queryKey: ['lead-edit', leadId],
    queryFn: () => loadEditTarget(leadId),
    // Ephemeral prefill: drop the moment the edit screen unmounts. This form
    // seeds react-hook-form's `defaultValues` (and the capture draft) exactly
    // once on mount and never `reset()`s them, so a cached-but-stale entry
    // returned synchronously on a SECOND edit would mount the form on the
    // pre-first-edit snapshot and silently revert every field the user did not
    // retype. gcTime 0 forces every entry into this screen to be a cold, fresh
    // fetch — the same correct path the first edit already takes.
    gcTime: 0,
    // `loadEditTarget` resolves an unsynced lead from Dexie, which is the whole
    // point of being able to edit one at a booth. TanStack's default
    // `networkMode: 'online'` would pause the query instead of running it.
    networkMode: 'always'
  });

  const targetId = target ? (target.kind === 'server' ? target.lead.id : target.clientUuid) : null;
  const targetItems = target ? (target.kind === 'server' ? target.lead.items : target.input.items) : null;

  // Seed the shared capture draft from the lead's existing items, once per
  // lead. The wire item shape carries no `itemNo`/`coverPath` — those are
  // catalogue-product display fields, not persisted per line item — so each is
  // looked up from the offline-precached Dexie `catalogue` mirror. A product
  // that has since been deleted from the catalogue (rare) falls back to
  // itemNo 0 / no photo rather than blocking the prefill.
  useEffect(() => {
    if (!targetItems || targetId === null || seededForRef.current === targetId) return;
    seededForRef.current = targetId;
    void (async () => {
      const draftItems: DraftItem[] = await Promise.all(
        targetItems.map(async item => {
          const product = await db.catalogue.get(item.productId);
          return {
            productId: item.productId,
            itemNo: product?.itemNo ?? 0,
            productNameSnapshot: item.productNameSnapshot,
            thumbPath: product?.photos[0]?.thumbPath ?? null,
            quantity: item.quantity,
            listPriceSnapshot: item.listPriceSnapshot ?? null,
            quotedPrice: item.quotedPrice,
            note: item.note ?? ''
          };
        })
      );
      setItems(draftItems);
    })();
  }, [targetId, targetItems, setItems]);

  // A queued lead's card lives in Dexie as bytes, not behind a media URL, so
  // its preview is an object URL this screen owns and must revoke.
  const draftCardUrls = useMemo(() => {
    const toUrl = (card: CardCapture | null) =>
      card ? URL.createObjectURL(new Blob([card.bytes], { type: card.contentType })) : null;
    return target?.kind === 'draft'
      ? { front: toUrl(target.front), back: toUrl(target.back) }
      : { front: null, back: null };
  }, [target]);
  useEffect(() => {
    return () => {
      for (const url of [draftCardUrls.front, draftCardUrls.back]) if (url) URL.revokeObjectURL(url);
    };
  }, [draftCardUrls]);

  if (isLoading) {
    return (
      <div className="grid h-dvh place-items-center text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <Spinner className="size-4" aria-hidden="true" /> Loading…
        </span>
      </div>
    );
  }

  if (!target || targetId === null) {
    return (
      <div className="grid h-dvh place-items-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">Could not load this buyer.</p>
          <Button variant="outline" onClick={() => void navigate({ to: '/' })}>
            ‹ Back to Buyers
          </Button>
        </div>
      </div>
    );
  }

  const isDraft = target.kind === 'draft';
  const lead = target.kind === 'server' ? target.lead : null;

  const backToDetail = () => navigate({ to: '/leads/$leadId', params: { leadId: targetId } });

  const handleConfirmArchive = async (): Promise<void> => {
    setArchiving(true);
    setArchiveError(null);
    const result = await archiveLead(targetId);
    setArchiving(false);
    if (result.kind === 'ok') {
      clearDraft();
      await navigate({ to: '/' });
      return;
    }
    setArchiveError(
      result.kind === 'offline'
        ? 'Lost connection while archiving. Try again once you are back online.'
        : result.kind === 'unauthenticated'
          ? 'Your session has expired. Sign in again to archive this buyer.'
          : result.message
    );
  };

  return (
    <>
      <LeadCaptureForm
        mode="edit"
        headTitle="Edit Buyer"
        leadRef={target.kind === 'server' ? target.lead.ref : target.ref}
        defaultValues={target.kind === 'server' ? toFormValues(target.lead) : valuesFromCreateInput(target.input)}
        initialCardFrontUrl={lead?.cardPhotoKey ? mediaUrl('cards', lead.cardPhotoKey) : draftCardUrls.front}
        initialCardBackUrl={lead?.cardBackKey ? mediaUrl('cards', lead.cardBackKey) : draftCardUrls.back}
        initialCardFrontOriginalUrl={
          lead?.cardFrontOriginalKey ? cardOriginalMediaUrl(lead.cardFrontOriginalKey) : null
        }
        initialCardBackOriginalUrl={lead?.cardBackOriginalKey ? cardOriginalMediaUrl(lead.cardBackOriginalKey) : null}
        saveLabel="Save"
        // A queued lead is edited in the outbox, so this one save genuinely
        // works with no connection — it is the create that has not gone out yet.
        saveDisabled={isOffline && !isDraft}
        saveDisabledReason="Editing needs a connection. Reconnect, then try again."
        onCancel={() => void backToDetail()}
        onSave={async submission => {
          const result =
            target.kind === 'server'
              ? await saveLeadEdit(target.lead, submission)
              : await saveDraftEdit(target, submission);
          if (result.ok) {
            // The detail screen we return to caches ['lead-detail', id] and the
            // board reads a Dexie mirror — neither updates itself after this
            // save. Invalidate the detail query and re-pull the mirror so the
            // edit is visible immediately, not one background refetch / boot later.
            // (A draft's board row is a live Dexie query and needs neither.)
            await queryClient.invalidateQueries({ queryKey: ['lead-detail', targetId] });
            if (!isDraft) void refreshLeadsMirror();
            await backToDetail();
          }
          return result;
        }}
        belowSections={
          /*
           * "Delete this buyer" (docs/design's `onDeleteLead`, editing-only) —
           * an archive, not a hard delete (coding-standards.md, Soft delete).
           * Rendered in the form's normal flow at the end of the body (above the
           * sticky Cancel/Save footer), matching the design mock's inline
           * placement after the Money section — not floated over the footer.
           *
           * Absent for a queued lead: archiving addresses a lead by its server
           * id, and there isn't one yet. Discarding it entirely is `/sync`'s job.
           */
          isDraft ? null : (
            <div className="mt-2 flex flex-col items-center gap-1 border-t border-dashed pt-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                disabled={isOffline || archiving}
                onClick={() => setConfirmArchiveOpen(true)}
              >
                {archiving ? 'Archiving…' : 'Delete this buyer'}
              </Button>
              {isOffline && <p className="text-xs text-muted-foreground">Archiving needs a connection.</p>}
              {archiveError && (
                <p role="alert" aria-live="polite" className="text-center text-xs text-destructive">
                  {archiveError}
                </p>
              )}
            </div>
          )
        }
      />

      <ConfirmDialog
        open={confirmArchiveOpen}
        onOpenChange={setConfirmArchiveOpen}
        title="Archive this buyer?"
        description="It moves to Archived and you can restore it anytime."
        confirmLabel="Archive"
        onConfirm={() => void handleConfirmArchive()}
      />
    </>
  );
}
